import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const departmentKeys = {
    all: ['departments'],
    lists: () => [...departmentKeys.all, 'list'],
    list: (orgId) => [...departmentKeys.lists(), orgId],
    types: () => [...departmentKeys.all, 'types'],
};

// Fetch department types
export function useDepartmentTypes() {
    return useQuery({
        queryKey: departmentKeys.types(),
        queryFn: () => apiHelper.getResource('/org/department-types/'),
    });
}

// Fetch departments for an organization
export function useDepartments(orgId) {
    return useQuery({
        queryKey: departmentKeys.list(orgId),
        queryFn: () => apiHelper.getResource('/org/department/', { orgId }),
        enabled: !!orgId,
    });
}

// Fetch departments for a project
export function useDepartmentsByProject(projectId) {
    return useQuery({
        queryKey: [...departmentKeys.all, 'project', projectId],
        queryFn: () => apiHelper.getResource(`/org/department/?project=${projectId}`),
        enabled: !!projectId,
    });
}

// Create department
export function useCreateDepartment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => apiHelper.postResource('/org/department/', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
        },
    });
}

// Update department
export function useUpdateDepartment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => apiHelper.putResource(`/org/department/${id}/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
        },
    });
}

// Delete department
export function useDeleteDepartment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.deleteResource(`/org/department/${id}/`, { orgId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
        },
    });
}

// ============ UNITS ============

export const unitKeys = {
    all: ['units'],
    lists: () => [...unitKeys.all, 'list'],
    list: (deptId) => [...unitKeys.lists(), deptId],
};

// Fetch units for a department
export function useUnits(deptId, orgId = null) {
    return useQuery({
        queryKey: unitKeys.list(deptId),
        queryFn: () => apiHelper.getResource(`/org/unit/?department=${deptId}`, { orgId }),
        enabled: !!deptId,
    });
}

// Create unit
export function useCreateUnit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => apiHelper.postResource('/org/unit/', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
        },
    });
}

// Update unit
export function useUpdateUnit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => apiHelper.putResource(`/org/unit/${id}/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
        },
    });
}

// Delete unit
export function useDeleteUnit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.deleteResource(`/org/unit/${id}/`, { orgId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
        },
    });
}
